import { Directive, Input, Output, EventEmitter, HostListener, ViewContainerRef, ElementRef, ReflectiveInjector, ComponentFactoryResolver, Renderer2 } from '@angular/core';
import { ConfirmationPopoverWindowComponent } from './confirmation-popover-window.component';
import { ConfirmationPopoverOptions } from './confirmation-popover-options.provider';
import { ConfirmationPopoverWindowOptions } from './confirmation-popover-window-options.provider';
import { Positioning } from 'positioning';
/**
 * All properties can be set on the directive as attributes like so (use `ConfirmationPopoverModule.forRoot()` to configure them globally):
 * ```html
 * <button
 *  class="btn btn-default"
 *  mwlConfirmationPopover
 *  [title]="title"
 *  [message]="message"
 *  placement="left"
 *  (confirm)="confirmClicked = true"
 *  (cancel)="cancelClicked = true"
 *  [(isOpen)]="isOpen">
 *   Show confirm popover!
 * </button>
 * ```
 */
var ConfirmationPopoverDirective = (function () {
    /**
     * @private
     */
    function ConfirmationPopoverDirective(viewContainerRef, elm, defaultOptions, cfr, position, renderer) {
        this.viewContainerRef = viewContainerRef;
        this.elm = elm;
        this.defaultOptions = defaultOptions;
        this.cfr = cfr;
        this.position = position;
        this.renderer = renderer;
        /**
         * Whether to disable showing the popover. Default `false`.
         */
        this.isDisabled = false;
        /**
         * Will open or show the popover when changed.
         * Can be sugared with `isOpenChange` to emulate 2-way binding like so `[(isOpen)]="isOpen"`
         */
        this.isOpen = false;
        /**
         * Will emit when the popover is opened or closed
         */
        this.isOpenChange = new EventEmitter(true);
        /**
         * An expression that is called when the confirm button is clicked.
         */
        this.confirm = new EventEmitter();
        /**
         * An expression that is called when the cancel button is clicked.
         */
        this.cancel = new EventEmitter();
        /**
         * @private
         */
        this.eventListeners = [];
    }
    /**
     * @private
     */
    ConfirmationPopoverDirective.prototype.ngOnInit = function () {
        this.isOpenChange.emit(false);
    };
    /**
     * @private
     */
    ConfirmationPopoverDirective.prototype.ngOnChanges = function (changes) {
        if (changes.isOpen) {
            if (changes.isOpen.currentValue === true) {
                this.showPopover();
            }
            else {
                this.hidePopover();
            }
        }
    };
    /**
     * @private
     */
    ConfirmationPopoverDirective.prototype.ngOnDestroy = function () {
        this.hidePopover();
    };
    /**
     * @private
     */
    ConfirmationPopoverDirective.prototype.onConfirm = function (event) {
        this.confirm.emit(event);
        this.hidePopover();
    };
    /**
     * @private
     */
    ConfirmationPopoverDirective.prototype.onCancel = function (event) {
        this.cancel.emit(event);
        this.hidePopover();
    };
    /**
     * @private
     */
    ConfirmationPopoverDirective.prototype.togglePopover = function () {
        if (!this.popover) {
            this.showPopover();
        }
        else {
            this.hidePopover();
        }
    };
    ConfirmationPopoverDirective.prototype.onDocumentClick = function (event) {
        if (this.popover &&
            !this.elm.nativeElement.contains(event.target) &&
            !this.popover.location.nativeElement.contains(event.target)) {
            this.hidePopover();
        }
    };
    ConfirmationPopoverDirective.prototype.showPopover = function () {
        var _this = this;
        if (!this.popover && !this.isDisabled) {
            // work around for https://github.com/mattlewis92/angular-confirmation-popover/issues/65
            // otherwise the document click event gets fired after the click event
            // that triggered the popover to open (no idea why this is so)
            setTimeout(function () {
                _this.eventListeners = [
                    _this.renderer.listen('document', 'click', function (event) {
                        return _this.onDocumentClick(event);
                    }),
                    _this.renderer.listen('document', 'touchend', function (event) {
                        return _this.onDocumentClick(event);
                    }),
                    _this.renderer.listen('window', 'resize', function () { return _this.positionPopover(); })
                ];
            });
            var options_1 = new ConfirmationPopoverWindowOptions();
            Object.assign(options_1, this.defaultOptions, {
                title: this.popoverTitle || this.title,
                message: this.popoverMessage || this.message,
                onConfirm: function (event) {
                    _this.onConfirm(event);
                },
                onCancel: function (event) {
                    _this.onCancel(event);
                },
                onAfterViewInit: function () {
                    _this.positionPopover();
                }
            });
            var optionalParams = [
                'confirmText',
                'cancelText',
                'placement',
                'confirmButtonType',
                'cancelButtonType',
                'focusButton',
                'hideConfirmButton',
                'hideCancelButton',
                'popoverClass',
                'appendToBody',
                'customTemplate'
            ];
            optionalParams.forEach(function (param) {
                if (typeof _this[param] !== 'undefined') {
                    options_1[param] = _this[param];
                }
            });
            var componentFactory = this.cfr.resolveComponentFactory(ConfirmationPopoverWindowComponent);
            var binding = ReflectiveInjector.resolve([
                {
                    provide: ConfirmationPopoverWindowOptions,
                    useValue: options_1
                }
            ]);
            var contextInjector = this.viewContainerRef.parentInjector;
            var childInjector = ReflectiveInjector.fromResolvedProviders(binding, contextInjector);
            this.popover = this.viewContainerRef.createComponent(componentFactory, this.viewContainerRef.length, childInjector);
            if (options_1.appendToBody) {
                document.body.appendChild(this.popover.location.nativeElement);
            }
            this.isOpenChange.emit(true);
        }
    };
    ConfirmationPopoverDirective.prototype.positionPopover = function () {
        if (this.popover) {
            var popoverElement = this.popover.location.nativeElement.children[0];
            var popoverPosition = this.position.positionElements(this.elm.nativeElement, popoverElement, this.placement || this.defaultOptions.placement, this.appendToBody || this.defaultOptions.appendToBody);
            this.renderer.setStyle(popoverElement, 'top', popoverPosition.top + "px");
            this.renderer.setStyle(popoverElement, 'left', popoverPosition.left + "px");
        }
    };
    ConfirmationPopoverDirective.prototype.hidePopover = function () {
        if (this.popover) {
            this.popover.destroy();
            delete this.popover;
            this.isOpenChange.emit(false);
            this.eventListeners.forEach(function (fn) { return fn(); });
            this.eventListeners = [];
        }
    };
    ConfirmationPopoverDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[mwlConfirmationPopover]'
                },] },
    ];
    /** @nocollapse */
    ConfirmationPopoverDirective.ctorParameters = function () { return [
        { type: ViewContainerRef, },
        { type: ElementRef, },
        { type: ConfirmationPopoverOptions, },
        { type: ComponentFactoryResolver, },
        { type: Positioning, },
        { type: Renderer2, },
    ]; };
    ConfirmationPopoverDirective.propDecorators = {
        'title': [{ type: Input },],
        'popoverTitle': [{ type: Input },],
        'message': [{ type: Input },],
        'popoverMessage': [{ type: Input },],
        'confirmText': [{ type: Input },],
        'cancelText': [{ type: Input },],
        'placement': [{ type: Input },],
        'confirmButtonType': [{ type: Input },],
        'cancelButtonType': [{ type: Input },],
        'focusButton': [{ type: Input },],
        'hideConfirmButton': [{ type: Input },],
        'hideCancelButton': [{ type: Input },],
        'isDisabled': [{ type: Input },],
        'isOpen': [{ type: Input },],
        'customTemplate': [{ type: Input },],
        'isOpenChange': [{ type: Output },],
        'confirm': [{ type: Output },],
        'cancel': [{ type: Output },],
        'popoverClass': [{ type: Input },],
        'appendToBody': [{ type: Input },],
        'togglePopover': [{ type: HostListener, args: ['click',] },],
    };
    return ConfirmationPopoverDirective;
}());
export { ConfirmationPopoverDirective };
//# sourceMappingURL=confirmation-popover.directive.js.map


//////////////////
// WEBPACK FOOTER
// ./~/angular-confirmation-popover/dist/esm/src/confirmation-popover.directive.js
// module id = 642
// module chunks = 9